import type { MarginInventorySnapshot, MarginTerm } from "@yutai/shared"
import { useEffect, useRef, useState } from "react"
import { decodeRakutenCsvFile } from "../lists/rakutenCsv.js"
import styles from "./MarginInventoryUploadPanel.module.css"
import { adminFetch } from "./marginInventoryApi.js"
import {
  MARGIN_TERM_LABELS,
  type ParsedMarginCsv,
  inferSnapshotDate,
  itemsForTerm,
  parseMarginInventoryCsv,
} from "./marginInventoryCsv.js"

interface Pending {
  filename: string
  parsed: ParsedMarginCsv
  snapshotDate: string
  term: MarginTerm | ""
}

function formatSnapshotDate(date: string): string {
  const [, m, d] = date.split("-")
  return `${Number(m)}/${Number(d)}`
}

function todayJst(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

function describeSnapshot(s: MarginInventorySnapshot): string {
  return `${formatSnapshotDate(s.snapshotDate)} ${s.allZero ? "在庫なし" : `${s.rowCount}件`}`
}

// 楽天証券の一般信用売建可能銘柄CSVをアップロードして、その日の在庫数として記録する。
// 一般信用在庫ページ（/admin/margin-inventory）の最上部に置く（毎日スマホから上げる想定）。
export function MarginInventoryUploadPanel({ onSaved }: { onSaved?: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [snapshots, setSnapshots] = useState<MarginInventorySnapshot[] | null>(null)

  async function loadSnapshots() {
    try {
      setSnapshots(await adminFetch<MarginInventorySnapshot[]>("/margin-inventory/snapshots"))
    } catch (e) {
      console.error(e)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    loadSnapshots()
  }, [])

  async function handleFile(file: File) {
    setError(null)
    setSuccess(null)
    setPending(null)
    try {
      const parsed = parseMarginInventoryCsv(await decodeRakutenCsvFile(file))
      setPending({
        filename: file.name,
        parsed,
        snapshotDate: inferSnapshotDate(file.name, file.lastModified),
        term: parsed.detectedTerm ?? "",
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function handleUpload() {
    if (!pending || !pending.term || !pending.snapshotDate) return
    const items = itemsForTerm(pending.parsed, pending.term)
    setUploading(true)
    setError(null)
    try {
      const result = await adminFetch<{
        rowCount: number
        skippedCount: number
        allZero: boolean
      }>("/margin-inventory/upload", {
        method: "POST",
        body: JSON.stringify({
          snapshotDate: pending.snapshotDate,
          term: pending.term,
          filename: pending.filename,
          items,
        }),
      })
      const stored = result.allZero ? "在庫なし" : `${result.rowCount}銘柄`
      const skipped = result.skippedCount > 0 ? `（優待なし${result.skippedCount}銘柄は除外）` : ""
      setSuccess(
        `✓ 登録しました: ${pending.snapshotDate} ${MARGIN_TERM_LABELS[pending.term]} ${stored}${skipped}`
      )
      setPending(null)
      loadSnapshots()
      onSaved?.()
    } catch (e) {
      setError(`登録に失敗しました: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setUploading(false)
    }
  }

  // CSVをダウンロードせずに「今日は全銘柄在庫なし」を記録する（権利日直後の14日在庫など）
  async function handleMarkEmpty(term: MarginTerm) {
    const date = todayJst()
    const label = MARGIN_TERM_LABELS[term]
    if (!window.confirm(`${date} の${label}在庫を「在庫なし（全銘柄0）」で記録しますか？`)) return
    setUploading(true)
    setError(null)
    setSuccess(null)
    try {
      await adminFetch("/margin-inventory/mark-empty", {
        method: "POST",
        body: JSON.stringify({ snapshotDate: date, term }),
      })
      setSuccess(`✓ 記録しました: ${date} ${label} 在庫なし`)
      loadSnapshots()
      onSaved?.()
    } catch (e) {
      setError(`記録に失敗しました: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setUploading(false)
    }
  }

  const latestByTerm = (["14d", "unlimited"] as const)
    .map((term) => snapshots?.find((s) => s.term === term))
    .filter((s): s is MarginInventorySnapshot => Boolean(s))

  const items = pending?.term ? itemsForTerm(pending.parsed, pending.term) : []
  const inStock = items.filter((i) => i.quantity > 0).length
  const overwrites =
    pending?.term &&
    snapshots?.some((s) => s.snapshotDate === pending.snapshotDate && s.term === pending.term)

  return (
    <section className={styles.panel}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>楽天CSVアップロード</h2>
        {latestByTerm.length > 0 && (
          <span className={styles.latest}>
            最終:{" "}
            {latestByTerm
              .map((s) => `${MARGIN_TERM_LABELS[s.term]} ${describeSnapshot(s)}`)
              .join(" / ")}
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className={styles.fileInput}
        onChange={(e) => {
          const file = e.target.files?.[0]
          // 同じファイルを選び直しても onChange が発火するようにリセット
          e.target.value = ""
          if (file) handleFile(file)
        }}
      />
      {!pending && (
        <>
          <button
            type="button"
            className={styles.uploadButton}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            在庫CSVをアップロード
          </button>
          <button
            type="button"
            className={styles.markEmptyButton}
            onClick={() => handleMarkEmpty("14d")}
            disabled={uploading}
          >
            今日の14日在庫は「なし」で記録
          </button>
        </>
      )}

      {error && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      {pending && (
        <div className={styles.confirm}>
          <p className={styles.filename}>{pending.filename}</p>
          <label className={styles.field}>
            <span>在庫日</span>
            <input
              type="date"
              value={pending.snapshotDate}
              onChange={(e) => setPending({ ...pending, snapshotDate: e.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span>弁済期限</span>
            <select
              value={pending.term}
              onChange={(e) => setPending({ ...pending, term: e.target.value as MarginTerm | "" })}
            >
              {!pending.parsed.detectedTerm && <option value="">選択してください</option>}
              <option value="14d">{MARGIN_TERM_LABELS["14d"]}</option>
              <option value="unlimited">{MARGIN_TERM_LABELS.unlimited}</option>
            </select>
          </label>
          {pending.term && (
            <p className={styles.summary}>
              {inStock === 0
                ? `${items.length}銘柄すべて在庫0のため「在庫なし」として記録します（銘柄ごとの行は保存しません）。`
                : `${items.length}銘柄（在庫あり ${inStock}件）を登録します。優待のない銘柄は保存時に除外されます。`}
              {overwrites && (
                <>
                  <br />
                  <strong>この日付・期限は登録済みのため上書きします。</strong>
                </>
              )}
            </p>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setPending(null)}
              disabled={uploading}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleUpload}
              disabled={uploading || !pending.term || !pending.snapshotDate || items.length === 0}
            >
              {uploading ? "登録中..." : "登録する"}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
